import { useCallback, useEffect, useRef, useState } from 'react';
import { ALL_TAUGHT_KEYS } from '../data/curriculum';
import {
  GUESS_CATEGORIES,
  MYSTERY_VARIANTS,
  SKRIVELEG_GAMES,
  formatSkrivelegDuration,
  mysteryVariantDef,
  skrivelegGameDef,
  starsLabel,
} from '../data/skriveleg/modes';
import {
  fetchSkrivelegChat,
  fetchSkrivelegForfeit,
  fetchSkrivelegGuess,
  fetchSkrivelegHint,
  fetchSkrivelegStart,
  type SkrivelegChatResult,
  type SkrivelegStartResult,
} from '../lib/skrivelegApi';
import {
  useSkrivelegDraftInput,
  type SkrivelegDraftStats,
} from '../lib/useSkrivelegDraftInput';
import { useEnterAction } from '../lib/useEnterAction';
import type {
  GuessCategoryId,
  KeyboardLayoutId,
  MysteryVariantId,
  SkrivelegChatMessage,
  SkrivelegGameId,
  SkrivelegGuessResult,
  SkrivelegPersuadeResultView,
  UserPreferencesPatch,
} from '../types';
import Keyboard from './Keyboard';

type Phase =
  | 'game'
  | 'variant'
  | 'category'
  | 'loading-start'
  | 'briefing'
  | 'chat'
  | 'guessing'
  | 'loading'
  | 'result'
  | 'error';

interface SkrivelegScreenProps {
  keyboardLayout: KeyboardLayoutId;
  initialGame?: SkrivelegGameId;
  initialMysteryVariant?: MysteryVariantId;
  initialGuessCategory?: GuessCategoryId;
  onPreferencesChange?: (patch: UserPreferencesPatch) => void;
  onFinish: () => void;
  onCancel: () => void;
}

export default function SkrivelegScreen({
  keyboardLayout,
  initialGame,
  initialMysteryVariant,
  initialGuessCategory,
  onPreferencesChange,
  onFinish,
  onCancel,
}: SkrivelegScreenProps) {
  const [phase, setPhase] = useState<Phase>('game');
  const [game, setGame] = useState<SkrivelegGameId | null>(null);
  const [mysteryVariant, setMysteryVariant] = useState<MysteryVariantId | null>(
    null,
  );
  const [guessCategory, setGuessCategory] = useState<GuessCategoryId | null>(
    null,
  );
  const [session, setSession] = useState<SkrivelegStartResult | null>(null);
  const [messages, setMessages] = useState<SkrivelegChatMessage[]>([]);
  const [remainingMs, setRemainingMs] = useState(0);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [hintsRemaining, setHintsRemaining] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mysteryResult, setMysteryResult] = useState<SkrivelegGuessResult | null>(
    null,
  );
  const [persuadeResult, setPersuadeResult] =
    useState<SkrivelegPersuadeResultView | null>(null);
  const [draftStats, setDraftStats] = useState<SkrivelegDraftStats | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const forfeitCalled = useRef(false);

  useEffect(() => {
    if (phase !== 'chat') return;
    const id = window.setInterval(() => {
      setRemainingMs((prev) => Math.max(0, prev - 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'chat' || remainingMs > 0 || forfeitCalled.current) return;
    const token = sessionTokenRef.current;
    if (!token || !session) return;
    forfeitCalled.current = true;
    void fetchSkrivelegForfeit({ sessionToken: token }).then((result) => {
      if ('secret' in result) {
        setMysteryResult(result);
      } else {
        setPersuadeResult(result);
      }
      setPhase('result');
    });
  }, [phase, remainingMs, session]);

  const startSession = useCallback(
    async (
      g: SkrivelegGameId,
      opts?: { mysteryVariant?: MysteryVariantId; guessCategoryId?: GuessCategoryId },
    ) => {
      setPhase('loading-start');
      setError(null);
      try {
        const result = await fetchSkrivelegStart({
          game: g,
          mysteryVariant: opts?.mysteryVariant,
          guessCategoryId: opts?.guessCategoryId,
        });
        sessionTokenRef.current = result.sessionToken;
        setSession(result);
        setRemainingMs(result.sessionDurationMs);
        setUserMessageCount(0);
        setHintsRemaining(2);
        const initial: SkrivelegChatMessage[] = [];
        if (result.openingMessage) {
          initial.push({ role: 'assistant', content: result.openingMessage });
        }
        setMessages(initial);
        setPhase('briefing');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Kunne ikke starte.');
        setPhase('error');
      }
    },
    [],
  );

  const handlePickGame = useCallback(
    (g: SkrivelegGameId) => {
      setGame(g);
      onPreferencesChange?.({ lastSkrivelegGame: g });
      if (g === 'mystery') {
        setPhase('variant');
      } else if (g === 'guess') {
        setPhase('category');
      } else {
        void startSession(g);
      }
    },
    [onPreferencesChange, startSession],
  );

  const handlePickVariant = useCallback(
    (v: MysteryVariantId) => {
      setMysteryVariant(v);
      onPreferencesChange?.({ lastMysteryVariant: v });
      if (game === 'mystery') void startSession('mystery', { mysteryVariant: v });
    },
    [game, onPreferencesChange, startSession],
  );

  const handlePickCategory = useCallback(
    (c: GuessCategoryId) => {
      setGuessCategory(c);
      onPreferencesChange?.({ lastGuessCategoryId: c });
      void startSession('guess', { guessCategoryId: c });
    },
    [onPreferencesChange, startSession],
  );

  const finishPersuade = useCallback(
    (won: boolean, count: number, opponent: string, goal: string) => {
      const stars = !won ? 1 : count <= 4 ? 3 : count <= 8 ? 2 : 1;
      setPersuadeResult({
        won,
        opponentName: opponent,
        goal,
        stars,
        messageCount: count,
        comment: won
          ? 'Du fik dem til at sige ja — imponerende retorik.'
          : 'De holdt stand den her gang. Prøv andre argumenter.',
      });
      setPhase('result');
    },
    [],
  );

  const handleChatResponse = useCallback(
    (result: SkrivelegChatResult) => {
      setRemainingMs(result.remainingMs);
      setUserMessageCount(result.userMessageCount);

      if (
        (session?.game === 'mystery' || session?.game === 'guess') &&
        result.guessResult
      ) {
        setMysteryResult(result.guessResult);
        setPhase('result');
        return;
      }

      if (session?.game === 'persuade' && result.done) {
        finishPersuade(
          result.convinced === true,
          result.userMessageCount,
          session.briefing.opponentName ?? 'Modparten',
          session.briefing.goal ?? '',
        );
        return;
      }

      if (result.remainingMs <= 0) {
        if (session?.game === 'mystery' || session?.game === 'guess') {
          setMysteryResult({
            correct: false,
            secret: session.briefing.secretSubject ?? '?',
            stars: 1,
            questionCount: result.userMessageCount,
          });
          setPhase('result');
        } else if (session) {
          finishPersuade(
            false,
            result.userMessageCount,
            session.briefing.opponentName ?? 'Modparten',
            session.briefing.goal ?? '',
          );
        }
      }
    },
    [session, finishPersuade],
  );

  const sendMessage = useCallback(
    async (text: string, stats: SkrivelegDraftStats) => {
      const token = sessionTokenRef.current;
      if (!token || loading) return;
      setDraftStats(stats);
      setMessages((prev) => [...prev, { role: 'user', content: text }]);
      setLoading(true);
      setError(null);
      try {
        const result = await fetchSkrivelegChat({
          sessionToken: token,
          message: text,
        });
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: result.reply },
        ]);
        handleChatResponse(result);
      } catch (e) {
        setMessages((prev) => prev.slice(0, -1));
        setError(e instanceof Error ? e.message : 'Kunne ikke sende.');
      } finally {
        setLoading(false);
      }
    },
    [loading, handleChatResponse],
  );

  const submitGuess = useCallback(
    async (guess: string, stats: SkrivelegDraftStats) => {
      const token = sessionTokenRef.current;
      if (!token) return;
      setDraftStats(stats);
      setLoading(true);
      try {
        const result = await fetchSkrivelegGuess({
          sessionToken: token,
          guess,
        });
        setMysteryResult(result);
        setPhase('result');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Kunne ikke gætte.');
        setPhase('error');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const requestHint = useCallback(async () => {
    const token = sessionTokenRef.current;
    if (!token || hintsRemaining <= 0 || loading) return;
    setLoading(true);
    try {
      const result = await fetchSkrivelegHint({ sessionToken: token });
      setHintsRemaining(result.hintsRemaining);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Hint: ${result.hint}` },
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke hente hint.');
    } finally {
      setLoading(false);
    }
  }, [hintsRemaining, loading]);

  const handleReset = useCallback(() => {
    sessionTokenRef.current = null;
    forfeitCalled.current = false;
    setSession(null);
    setMessages([]);
    setMysteryResult(null);
    setPersuadeResult(null);
    setError(null);
    setGame(null);
    setMysteryVariant(null);
    setGuessCategory(null);
    setPhase('game');
  }, []);

  const handlePlayAgain = useCallback(() => {
    sessionTokenRef.current = null;
    forfeitCalled.current = false;
    setSession(null);
    setMessages([]);
    setMysteryResult(null);
    setPersuadeResult(null);
    setError(null);
    if (game === 'persuade') {
      void startSession('persuade');
    } else if (game === 'mystery' && mysteryVariant) {
      void startSession('mystery', { mysteryVariant });
    } else if (game === 'mystery') {
      setPhase('variant');
    } else if (game === 'guess' && guessCategory) {
      void startSession('guess', { guessCategoryId: guessCategory });
    } else if (game === 'guess') {
      setPhase('category');
    } else {
      setPhase('game');
    }
  }, [game, mysteryVariant, guessCategory, startSession]);

  if (phase === 'game') {
    return (
      <GamePicker
        onPick={handlePickGame}
        onCancel={onCancel}
        initialHighlight={initialGame}
      />
    );
  }

  if (phase === 'variant') {
    return (
      <VariantPicker
        onPick={handlePickVariant}
        onBack={handleReset}
        onCancel={onCancel}
        initialHighlight={initialMysteryVariant}
      />
    );
  }

  if (phase === 'category') {
    return (
      <CategoryPicker
        onPick={handlePickCategory}
        onBack={handleReset}
        onCancel={onCancel}
        initialHighlight={initialGuessCategory}
      />
    );
  }

  if (phase === 'loading-start' || phase === 'loading') {
    return <LoadingView message="Forbereder legen…" onCancel={onCancel} />;
  }

  if (phase === 'error') {
    return (
      <ErrorView
        message={error ?? 'Noget gik galt.'}
        onRetry={handlePlayAgain}
        onCancel={onCancel}
      />
    );
  }

  if (phase === 'briefing' && session) {
    return (
      <BriefingView
        session={session}
        onStart={() => setPhase('chat')}
        onBack={handleReset}
        onCancel={onCancel}
      />
    );
  }

  if (phase === 'chat' && session) {
    return (
      <ChatView
        keyboardLayout={keyboardLayout}
        session={session}
        messages={messages}
        remainingMs={remainingMs}
        userMessageCount={userMessageCount}
        maxMessages={session.maxUserMessages}
        hintsRemaining={hintsRemaining}
        loading={loading}
        error={error}
        onSend={sendMessage}
        onRequestHint={requestHint}
        onGuess={() => setPhase('guessing')}
        onCancel={onCancel}
      />
    );
  }

  if (phase === 'guessing' && session) {
    return (
      <GuessView
        keyboardLayout={keyboardLayout}
        onSubmit={submitGuess}
        onCancel={() => setPhase('chat')}
      />
    );
  }

  if (phase === 'result') {
    return (
      <ResultView
        game={session?.game ?? game ?? 'mystery'}
        mysteryResult={mysteryResult}
        persuadeResult={persuadeResult}
        stats={draftStats}
        onPlayAgain={handlePlayAgain}
        onChangeGame={handleReset}
        onFinish={onFinish}
      />
    );
  }

  return null;
}

function GamePicker({
  onPick,
  onCancel,
  initialHighlight,
}: {
  onPick: (g: SkrivelegGameId) => void;
  onCancel: () => void;
  initialHighlight?: SkrivelegGameId;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Ekstra øvelse · Eksperiment</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">Skriveleg</h1>
        <p className="mt-2 text-sm text-muted">
          Chat med AI — gæt hemmeligheder eller overbevis modstandere.
        </p>
        <p className="mt-1 text-xs text-faint">
          Dine beskeder sendes til AI for legen og gemmes ikke.
        </p>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        {SKRIVELEG_GAMES.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => onPick(g.id)}
            className={`flex flex-col gap-2 rounded-xl px-4 py-4 text-left ring-1 transition hover:bg-indigo-50 dark:hover:bg-indigo-500/10 ${
              initialHighlight === g.id
                ? 'bg-indigo-50 ring-indigo-300 dark:bg-indigo-500/15 dark:ring-indigo-500/40'
                : 'bg-indigo-50/70 ring-indigo-200 dark:bg-indigo-500/10 dark:ring-indigo-500/30'
            }`}
          >
            <span className="text-2xl" aria-hidden>
              {g.emoji}
            </span>
            <span className="text-sm font-semibold text-strong">{g.title}</span>
            <span className="text-xs text-muted">{g.description}</span>
          </button>
        ))}
      </div>
      <button type="button" onClick={onCancel} className="btn-secondary self-start">
        Tilbage
      </button>
    </div>
  );
}

function VariantPicker({
  onPick,
  onBack,
  onCancel,
  initialHighlight,
}: {
  onPick: (v: MysteryVariantId) => void;
  onBack: () => void;
  onCancel: () => void;
  initialHighlight?: MysteryVariantId;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Skriveleg · Mystery</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">Vælg type</h1>
        <p className="mt-2 text-sm text-muted">
          Person, sted eller genstand — spørg ind til det hemmelige svar.
        </p>
      </header>
      <div className="grid gap-3 sm:grid-cols-3">
        {MYSTERY_VARIANTS.map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => onPick(v.id)}
            className={`flex flex-col gap-2 rounded-xl px-4 py-4 text-left ring-1 transition hover:bg-zinc-100 dark:hover:bg-zinc-900 ${
              initialHighlight === v.id
                ? 'bg-zinc-100 ring-zinc-400 dark:bg-zinc-900 dark:ring-zinc-500'
                : 'bg-zinc-50 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5'
            }`}
          >
            <span className="text-2xl" aria-hidden>
              {v.emoji}
            </span>
            <span className="text-sm font-semibold text-strong">{v.title}</span>
            <span className="text-xs text-muted">{v.description}</span>
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        <button type="button" onClick={onBack} className="btn-secondary">
          Skift leg
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annuller
        </button>
      </div>
    </div>
  );
}

function CategoryPicker({
  onPick,
  onBack,
  onCancel,
  initialHighlight,
}: {
  onPick: (c: GuessCategoryId) => void;
  onBack: () => void;
  onCancel: () => void;
  initialHighlight?: GuessCategoryId;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Skriveleg · Gæt mit emne</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">Vælg kategori</h1>
        <p className="mt-2 text-sm text-muted">
          Du får et hemmeligt emne — beskriv det uden at nævne ordet. AI gætter
          efter hver besked.
        </p>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        {GUESS_CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(c.id)}
            className={`flex flex-col gap-1 rounded-xl px-4 py-3 text-left ring-1 transition hover:bg-zinc-100 dark:hover:bg-zinc-900 ${
              initialHighlight === c.id
                ? 'bg-zinc-100 ring-zinc-400 dark:bg-zinc-900 dark:ring-zinc-500'
                : 'bg-zinc-50 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5'
            }`}
          >
            <span className="text-sm font-semibold text-strong">{c.label}</span>
            <span className="text-xs text-muted">{c.hint}</span>
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        <button type="button" onClick={onBack} className="btn-secondary">
          Skift leg
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annuller
        </button>
      </div>
    </div>
  );
}

function BriefingView({
  session,
  onStart,
  onBack,
  onCancel,
}: {
  session: SkrivelegStartResult;
  onStart: () => void;
  onBack: () => void;
  onCancel: () => void;
}) {
  useEnterAction(onStart, true);
  const def =
    session.game === 'mystery' && session.mysteryVariant
      ? mysteryVariantDef(session.mysteryVariant)
      : skrivelegGameDef(session.game);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Skriveleg · {def?.title ?? session.briefing.title}</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">Klar?</h1>
        {session.apiMode === 'mock' && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            Demo-tilstand — forudsigelige svar uden OpenAI-nøgle.
          </p>
        )}
      </header>
      <section className="surface space-y-3 p-5">
        {session.game === 'guess' && session.briefing.secretSubject && (
          <div className="rounded-lg bg-indigo-50 px-4 py-3 dark:bg-indigo-500/10">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
              Dit hemmelige emne
            </p>
            <p className="mt-1 text-xl font-semibold text-strong">
              {session.briefing.secretSubject}
            </p>
          </div>
        )}
        {session.game === 'persuade' && session.briefing.opponentName && (
          <p className="text-sm font-medium text-strong">
            Modpart: {session.briefing.opponentName}
          </p>
        )}
        {session.game === 'persuade' && session.briefing.persuadeHint && (
          <div className="rounded-lg bg-amber-50 px-4 py-3 dark:bg-amber-500/10">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
              Tænk over
            </p>
            <p className="mt-1 text-sm text-strong">
              {session.briefing.persuadeHint}
            </p>
          </div>
        )}
        <p className="text-sm text-muted">{session.briefing.instruction}</p>
        {session.openingMessage && (
          <div className="rounded-lg bg-zinc-50 p-3 dark:bg-zinc-900/60">
            <p className="label mb-1">Første besked</p>
            <p className="text-sm text-strong">{session.openingMessage}</p>
          </div>
        )}
        <p className="text-xs text-faint">
          Max {session.maxUserMessages} beskeder ·{' '}
          {formatSkrivelegDuration(session.sessionDurationMs)} · Enter sender
        </p>
      </section>
      <div className="flex justify-between gap-3">
        <button type="button" onClick={onBack} className="btn-secondary">
          Skift leg
        </button>
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Annuller
          </button>
          <button type="button" onClick={onStart} className="btn-primary">
            Start chat
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatView({
  keyboardLayout,
  session,
  messages,
  remainingMs,
  userMessageCount,
  maxMessages,
  hintsRemaining,
  loading,
  error,
  onSend,
  onRequestHint,
  onGuess,
  onCancel,
}: {
  keyboardLayout: KeyboardLayoutId;
  session: SkrivelegStartResult;
  messages: SkrivelegChatMessage[];
  remainingMs: number;
  userMessageCount: number;
  maxMessages: number;
  hintsRemaining: number;
  loading: boolean;
  error: string | null;
  onSend: (text: string, stats: SkrivelegDraftStats) => void;
  onRequestHint: () => void;
  onGuess: () => void;
  onCancel: () => void;
}) {
  const enabled = !loading && userMessageCount < maxMessages && remainingMs > 0;
  const input = useSkrivelegDraftInput(enabled, (text, stats) => {
    onSend(text, stats);
  });
  const messageListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = messageListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const title =
    session.game === 'mystery' && session.mysteryVariant
      ? mysteryVariantDef(session.mysteryVariant)?.title
      : skrivelegGameDef(session.game)?.title;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <header>
        <p className="label">Skriveleg · {title}</p>
        {session.game === 'guess' && session.briefing.secretSubject && (
          <p className="mt-1 text-sm text-muted">
            Emne:{' '}
            <span className="font-semibold text-strong">
              {session.briefing.secretSubject}
            </span>{' '}
            — nævn det ikke direkte.
          </p>
        )}
        {session.game === 'persuade' && session.briefing.persuadeHint && (
          <p className="mt-1 text-sm text-muted">
            Tænk over:{' '}
            <span className="text-strong">{session.briefing.persuadeHint}</span>
          </p>
        )}
        <div className="mt-1 flex flex-wrap gap-3 text-xs text-faint">
          <span>{userMessageCount}/{maxMessages} beskeder</span>
          <span>{formatSkrivelegDuration(remainingMs)} tilbage</span>
          <span>{input.stats.wpm.toFixed(0)} WPM</span>
        </div>
      </header>

      <div
        ref={messageListRef}
        className="surface max-h-[20rem] min-h-[12rem] space-y-3 overflow-y-auto p-4"
        aria-live="polite"
      >
        {messages.length === 0 && (
          <p className="text-sm text-faint">Stil dit første spørgsmål…</p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`rounded-lg px-3 py-2 text-sm ${
              m.role === 'user'
                ? 'ml-8 bg-indigo-50 text-strong dark:bg-indigo-500/15'
                : 'mr-8 bg-zinc-50 text-strong dark:bg-zinc-900/60'
            }`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-faint">
              {m.role === 'user' ? 'Dig' : 'AI'}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap">{m.content}</p>
          </div>
        ))}
        {loading && (
          <p className="text-xs text-faint animate-pulse">AI skriver…</p>
        )}
      </div>

      <div className="rounded-xl bg-zinc-50 p-3 ring-1 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5">
        {input.draft.length === 0 ? (
          <span className="text-faint">Skriv din besked…</span>
        ) : (
          <span className="whitespace-pre-wrap text-strong">{input.draft}</span>
        )}
      </div>

      {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}

      <div className="flex flex-wrap gap-2">
        {session.game === 'mystery' && (
          <>
            <button
              type="button"
              onClick={onGuess}
              disabled={loading}
              className="btn-secondary text-sm"
            >
              Jeg gætter!
            </button>
            <button
              type="button"
              onClick={onRequestHint}
              disabled={loading || hintsRemaining <= 0}
              className="btn-secondary text-sm"
            >
              Hint ({hintsRemaining})
            </button>
          </>
        )}
        {session.game === 'guess' && (
          <span className="self-center text-xs text-muted">
            AI gætter efter hver besked
          </span>
        )}
        <span className="self-center text-xs text-faint">Enter = send</span>
      </div>

      <Keyboard
        layoutId={keyboardLayout}
        unlockedKeys={ALL_TAUGHT_KEYS}
        hintLevel="none"
        showFingerDot={false}
      />

      <button type="button" onClick={onCancel} className="btn-secondary self-start">
        Afslut
      </button>
    </div>
  );
}

function GuessView({
  keyboardLayout,
  onSubmit,
  onCancel,
}: {
  keyboardLayout: KeyboardLayoutId;
  onSubmit: (guess: string, stats: SkrivelegDraftStats) => void;
  onCancel: () => void;
}) {
  const input = useSkrivelegDraftInput(true, (text, stats) => {
    onSubmit(text, stats);
  });
  useEnterAction(() => {
    if (input.draft.trim()) onSubmit(input.draft.trim(), input.stats);
  }, input.draft.trim().length > 0);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <header>
        <p className="label">Skriveleg · Mystery</p>
        <h1 className="mt-1 text-xl font-semibold text-strong">Hvem/hvad er det?</h1>
        <p className="mt-1 text-sm text-muted">Skriv dit gæt og tryk Enter.</p>
      </header>
      <div className="rounded-xl bg-indigo-50 p-4 text-lg dark:bg-indigo-500/10">
        {input.draft || (
          <span className="text-faint">Fx "Anders And" eller "Tivoli"…</span>
        )}
      </div>
      <Keyboard
        layoutId={keyboardLayout}
        unlockedKeys={ALL_TAUGHT_KEYS}
        hintLevel="none"
        showFingerDot={false}
      />
      <div className="flex gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Tilbage til chat
        </button>
      </div>
    </div>
  );
}

function ResultView({
  game,
  mysteryResult,
  persuadeResult,
  stats,
  onPlayAgain,
  onChangeGame,
  onFinish,
}: {
  game: SkrivelegGameId;
  mysteryResult: SkrivelegGuessResult | null;
  persuadeResult: SkrivelegPersuadeResultView | null;
  stats: SkrivelegDraftStats | null;
  onPlayAgain: () => void;
  onChangeGame: () => void;
  onFinish: () => void;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Skriveleg · Resultat</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          {game === 'guess'
            ? mysteryResult?.correct
              ? 'AI gættede det!'
              : 'AI nåede det ikke'
            : game === 'mystery'
              ? mysteryResult?.correct
                ? 'Rigtigt gæt!'
                : 'Ikke helt…'
              : persuadeResult?.won
                ? 'Du overbeviste dem!'
                : 'De sagde nej'}
        </h1>
      </header>

      {stats && (
        <div className="grid grid-cols-2 gap-3">
          <Stat label="WPM" value={stats.wpm.toFixed(0)} />
          <Stat
            label="Flyd"
            value={`${(stats.fluency * 100).toFixed(0)}%`}
          />
        </div>
      )}

      {mysteryResult && (
        <section className="surface space-y-3 p-5">
          <p className="text-sm">
            Svaret var:{' '}
            <span className="font-semibold text-strong">
              {mysteryResult.secret}
            </span>
          </p>
          <p className="text-sm text-muted">
            {game === 'guess'
              ? `${mysteryResult.questionCount} beskeder til AI gættede`
              : `${mysteryResult.questionCount} spørgsmål/beskeder`}
          </p>
          <p className="text-lg text-amber-500" aria-label={`${mysteryResult.stars} stjerner`}>
            {starsLabel(mysteryResult.stars)}
          </p>
        </section>
      )}

      {persuadeResult && (
        <section className="surface space-y-3 p-5">
          <p className="text-sm text-muted">{persuadeResult.comment}</p>
          <p className="text-sm">
            Modpart:{' '}
            <span className="font-semibold text-strong">
              {persuadeResult.opponentName}
            </span>
          </p>
          <p className="text-sm text-muted">
            {persuadeResult.messageCount} beskeder
          </p>
          <p className="text-lg text-amber-500" aria-label={`${persuadeResult.stars} stjerner`}>
            {starsLabel(persuadeResult.stars)}
          </p>
        </section>
      )}

      <div className="flex flex-wrap justify-between gap-3">
        <button type="button" onClick={onChangeGame} className="btn-secondary">
          Skift leg
        </button>
        <div className="flex gap-3">
          <button type="button" onClick={onPlayAgain} className="btn-secondary">
            Spil igen
          </button>
          <button type="button" onClick={onFinish} className="btn-primary">
            Færdig
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingView({
  message,
  onCancel,
}: {
  message: string;
  onCancel: () => void;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-6 py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      <p className="text-sm text-muted">{message}</p>
      <button type="button" onClick={onCancel} className="btn-secondary">
        Annuller
      </button>
    </div>
  );
}

function ErrorView({
  message,
  onRetry,
  onCancel,
}: {
  message: string;
  onRetry: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header>
        <p className="label">Skriveleg</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">Noget gik galt</h1>
        <p className="mt-2 text-sm text-muted">{message}</p>
      </header>
      <div className="flex gap-3">
        <button type="button" onClick={onRetry} className="btn-primary">
          Prøv igen
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Tilbage
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface p-3 text-center">
      <p className="text-xs text-faint">{label}</p>
      <p className="mt-0.5 font-mono text-lg font-semibold text-strong">{value}</p>
    </div>
  );
}
